import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useAuth } from '../hooks/useAuth';
import LogoutLink from '../components/LogoutLink';
import ActionModal from '../components/ActionModal';
import ToastNotification from '../components/ToastNotification';

import { RESET } from '../reducers/actionReducer';
import { useActionModal } from '../hooks/useActionModal';

import { isAdmin, isFinanceAdmin, isAnyAdmin } from '../utils';
import ProjectNavLink from '../components/ProjectNavLink';

export default function AdminRoutes() {
  const { user } = useAuth();
  const location = useLocation();

  const { state: actionState, dispatch: dispatchAction } = useActionModal();

  useEffect(() => {
    if (actionState.success) {
      setTimeout(
        () =>
          dispatchAction({
            type: RESET,
          }),
        5000
      );
    }
  }, [actionState.success, dispatchAction]);

  if (!isAnyAdmin(user)) {
    return <Navigate to="/dashboard" />;
  }

  if (isFinanceAdmin(user) && location.pathname === '/admin/') {
    return <Navigate to="/admin/orders" />;
  }

  const isFullAdmin = isAdmin(user);

  // Restrict finance-admin to the admin routes it's allowed: Orders and the
  // Ask Addy usage panel. Any other admin route bounces back to Orders.
  const FINANCE_ADMIN_PATHS = ['/admin/orders', '/admin/ask-addy-usage'];
  if (
    isFinanceAdmin(user) &&
    !FINANCE_ADMIN_PATHS.some((p) => location.pathname.includes(p))
  ) {
    return <Navigate to="/admin/orders" />;
  }

  return (
    <div className="p-5 flex flex-col h-screen">
      <div className="flex flex-grow-0 justify-between items-center border-b-2 border-black pb-2 px-1">
        <div className="flex space-x-6">
          <h1 className="mb-0">Admin Panel</h1>
          <div className="flex items-end pb-0.5 space-x-2">
            {isFullAdmin && (
              <>
                <ProjectNavLink to="/admin/" text="All Users" end={true} />
                <span>|</span>
                <ProjectNavLink to="maintenance" text="Maintenance" />
                <span>|</span>
                <a href="/api/jobs/" className="hover:text-primary">
                  Job Scheduling
                </a>
                <span>|</span>
              </>
            )}
            <ProjectNavLink to="orders" text="Orders" />
            {isFullAdmin && (
              <>
                <span>|</span>
                <ProjectNavLink to="ratecards" text="Ratecards" />
              </>
            )}
            {/* Ask Addy usage is open to finance-admin too (both roles reach
                here via the isAnyAdmin guard above). */}
            <span>|</span>
            <ProjectNavLink to="ask-addy-usage" text="Ask Addy" />
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span>Signed in as: {user.email}</span>
          <LogoutLink className="pt-1" />
        </div>
      </div>
      <div className="relative flex flex-grow overflow-auto">
        <div className="pt-2 w-full">
          <Outlet />
        </div>
      </div>
      {actionState.inProgress && (
        <ActionModal
          actionState={actionState}
          dispatchAction={dispatchAction}
          show={actionState.inProgress}
        />
      )}
      <ToastNotification
        message={actionState.success}
        success={true}
        show={actionState.success}
      />
    </div>
  );
}